import type { ReactNode } from 'react'
import { IconInbox } from '@tabler/icons-react'
import type { DataTableColumn } from '../types'

/* Table and its empty/loading states. Exports only components. */

/** A shimmer placeholder. Honours prefers-reduced-motion (see primitives.css). */
export function Skeleton({ width = '100%', height = 12, radius = 4 }: {
  width?: number | string
  height?: number | string
  radius?: number
}) {
  return <span className="lh-skeleton" style={{ display: 'block', width, height, borderRadius: radius }} />
}

/** Several skeleton lines, for a paragraph or list placeholder. */
export function SkeletonText({ lines = 3 }: { lines?: number }) {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {Array.from({ length: lines }, (_, i) => (
        // Last line short, so the block reads as text rather than as bars.
        <Skeleton key={i} width={i === lines - 1 ? '60%' : '100%'} />
      ))}
    </span>
  )
}

/**
 * Typographic empty state — no illustration by design (W1 ships none), so this
 * is the single place "there is nothing here" gets said.
 */
export function EmptyState({ title, message, icon, action }: {
  title: ReactNode
  message?: ReactNode
  icon?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="lh-empty">
      <span className="lh-empty-icon">{icon ?? <IconInbox size={28} stroke={1.5} />}</span>
      <span className="lh-empty-title">{title}</span>
      {message && <span className="lh-empty-message">{message}</span>}
      {action}
    </div>
  )
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string
  onRowClick?: (row: T) => void
  /** Renders skeleton rows instead of data. */
  loading?: boolean
  skeletonRows?: number
  /** Shown when `rows` is empty and not loading. */
  empty?: ReactNode
  /** Search / filter-chip row above the table. */
  toolbar?: ReactNode
}

/**
 * The registry table.
 *
 * Wide content scrolls inside its own container so the page body never scrolls
 * sideways. Numeric columns get tabular figures and right alignment — columns of
 * figures have to align vertically to be scannable.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  loading,
  skeletonRows = 5,
  empty,
  toolbar,
}: DataTableProps<T>) {
  const showEmpty = !loading && rows.length === 0

  return (
    <div>
      {toolbar && <div className="lh-table-toolbar">{toolbar}</div>}
      {showEmpty ? (
        empty ?? <EmptyState title="Nothing to show" />
      ) : (
        <div className="lh-table-wrap">
          <table className="lh-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key} style={col.width ? { width: col.width } : undefined}
                      data-numeric={col.numeric ? 'true' : undefined}>
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: skeletonRows }, (_, i) => (
                    <tr key={`skeleton-${i}`}>
                      {columns.map((col) => (
                        <td key={col.key}><Skeleton /></td>
                      ))}
                    </tr>
                  ))
                : rows.map((row) => (
                    <tr
                      key={rowKey(row)}
                      data-clickable={onRowClick ? 'true' : undefined}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                    >
                      {columns.map((col) => (
                        <td key={col.key} data-numeric={col.numeric ? 'true' : undefined}>
                          {col.render(row)}
                        </td>
                      ))}
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
